import { DateTime } from '@ksyos/date-time';
import { Button, FormGroup, Modal, Text } from '@ksyos/design-system';
import type { ValidationErrors } from '@frontend/utils/validation';
import { IndependentDateInput } from '@frontend/components/fields';
import { translateKey } from '@frontend/translate';
import type { IPatient } from '../PatientsAddEdit';

interface IPatientDeathModal {
    deceasedDate: string | null;
    getDateValidationError: (newDate: string | null) => 'koi_date' | 'date_future' | undefined;
    isPatientDeathModalOpen: boolean;
    patient: IPatient | null;
    setDeceasedDate: React.Dispatch<React.SetStateAction<string | null>>;
    setIsPatientDeathModalOpen: (isPatientDeathModalOpen: boolean) => void;
    setValidationErrors: React.Dispatch<React.SetStateAction<ValidationErrors>>;
    validationErrors: ValidationErrors;
}

export function PatientDeathModal(props: IPatientDeathModal) {
    const {
        deceasedDate,
        isPatientDeathModalOpen,
        setIsPatientDeathModalOpen,
        setDeceasedDate,
        setValidationErrors,
        patient,
        validationErrors,
        getDateValidationError,
    } = props;

    return (
        <Modal
            isOpen={isPatientDeathModalOpen}
            onClose={() => {
                setIsPatientDeathModalOpen(false);
                setDeceasedDate(patient?.deceasedDate ?? null);
                setValidationErrors(validationErrors.remove('deceasedDate'));
            }}
            size="medium"
        >
            <Modal.Header>{translateKey('patient.registerDeath')}</Modal.Header>
            <Modal.CloseButton />
            <Modal.Body>
                <Text paddingBottom={8}>Je staat op het punt om deze patiënt als overleden te registreren.</Text>
                <FormGroup label={translateKey('patient.deceasedDate')}>
                    <IndependentDateInput
                        error={validationErrors.get('deceasedDate')}
                        hasMaxWidth={true}
                        id="deceasedDate"
                        maxDate={DateTime.now().format()}
                        onChange={(value) => updateAndValidateDeceasedDate(value)}
                        onEnterPress={(value) => updateAndValidateDeceasedDate(value)}
                        placeholder={translateKey('common.datePlaceholder')}
                        size="large"
                        value={deceasedDate!}
                        variant="outline"
                    />
                </FormGroup>
            </Modal.Body>
            <Modal.Footer>
                <Button
                    onClick={() => {
                        setIsPatientDeathModalOpen(false);
                        setDeceasedDate(patient?.deceasedDate ?? null);
                    }}
                    variant="outline"
                >
                    {translateKey('common.cancel')}
                </Button>
                <Button
                    isDisabled={!deceasedDate}
                    onClick={() => handleSubmitPatientDeathModal()}
                    type="submit"
                    variant="solid"
                >
                    {translateKey('common.save')}
                </Button>
            </Modal.Footer>
        </Modal>
    );

    function handleSubmitPatientDeathModal() {
        setValidationErrors(validationErrors.remove('deceasedDate'));
        setIsPatientDeathModalOpen(false);
    }

    function updateAndValidateDeceasedDate(date: string | null) {
        const dateErrors = getDateValidationError(date);
        if (dateErrors) {
            setValidationErrors(validationErrors.set('deceasedDate', dateErrors));
        }
        if (!dateErrors) {
            setValidationErrors(validationErrors.remove('deceasedDate'));
            setDeceasedDate(date);
        }
    }
}
